import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { ContactFormData } from '../../types/contact';
import { submitContactForm } from '../../services/contactApi';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>();

  const onSubmit: SubmitHandler<ContactFormData> = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await submitContactForm(data);
      if (response.success) {
        setSubmitStatus({ type: 'success', message: response.message });
        reset();
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
        }, 2000);
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to send message. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      setSubmitStatus(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-bold text-charcoal dark:text-slate-100">Contact Us</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 text-2xl"
            disabled={isSubmitting}
            aria-label="Close contact form"
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Name
            </label>
            <input
              id="name"
              type="text"
              {...register('name', {
                required: 'Name is required',
                minLength: { value: 1, message: 'Name must be at least 1 character' },
                maxLength: { value: 100, message: 'Name must be less than 100 characters' },
              })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-sa-green focus:border-transparent dark:bg-slate-700 dark:text-slate-100"
              placeholder="Your name"
              disabled={isSubmitting}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-error">{errors.name.message}</p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-sa-green focus:border-transparent dark:bg-slate-700 dark:text-slate-100"
              placeholder="your.email@example.com"
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-error">{errors.email.message}</p>
            )}
          </div>

          {/* Subject Field */}
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Subject
            </label>
            <input
              id="subject"
              type="text"
              {...register('subject', {
                required: 'Subject is required',
                minLength: { value: 1, message: 'Subject must be at least 1 character' },
                maxLength: { value: 200, message: 'Subject must be less than 200 characters' },
              })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-sa-green focus:border-transparent dark:bg-slate-700 dark:text-slate-100"
              placeholder="What is this about?"
              disabled={isSubmitting}
            />
            {errors.subject && (
              <p className="mt-1 text-sm text-error">{errors.subject.message}</p>
            )}
          </div>

          {/* Message Field */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Message
            </label>
            <textarea
              id="message"
              rows={5}
              {...register('message', {
                required: 'Message is required',
                minLength: { value: 10, message: 'Message must be at least 10 characters' },
                maxLength: { value: 5000, message: 'Message must be less than 5000 characters' },
              })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-sa-green focus:border-transparent dark:bg-slate-700 dark:text-slate-100 resize-y"
              placeholder="Tell us what's on your mind..."
              disabled={isSubmitting}
            />
            {errors.message && (
              <p className="mt-1 text-sm text-error">{errors.message.message}</p>
            )}
          </div>

          {/* Status Message */}
          {submitStatus && (
            <div
              className={`p-3 rounded-lg text-center ${
                submitStatus.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/30 text-sa-green'
                  : 'bg-red-50 dark:bg-red-900/30 text-error'
              }`}
            >
              {submitStatus.message}
            </div>
          )}

          {/* Loading State */}
          {isSubmitting && (
            <div className="flex items-center justify-center space-x-2 py-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-sa-green"></div>
              <span className="text-gray-600 dark:text-slate-400">Sending...</span>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="btn-secondary px-6"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary px-6"
            >
              Send Message
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
